class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.resolvedSearchQuery = null;
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('submit', this.onSubmit.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }
  }
  getQuery() {
    return this.input.value.trim();
  }

  getFocusedSearchQuery() {
    const query = this.getQuery().replace(/\s+/g, ' ');

    if (!query || query.startsWith('title:')) {
      return query;
    }

    // Search product titles first. This avoids Shopify's fuzzy matches such as
    // "taçlı" returning "tavşanlı" while preserving natural multi-word searches.
    return `title:${query}`;
  }

  getSearchQuery() {
    return this.resolvedSearchQuery || this.getFocusedSearchQuery();
  }

  onSubmit(event) {
    if (this.getQuery().length === 0) {
      event.preventDefault();
      return;
    }

    this.input.value = this.getSearchQuery();
  }
  clear(event = null) {
    event && event.preventDefault();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
  }

  setupURL() {
    const url = new URL(`${FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`);
    return (
      url.searchParams.set('q', this.getFocusedSearchQuery()),
      url.searchParams.set('resources[type]', 'product'),
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title'),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    this.resolvedSearchQuery = null;
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const cachedResult = this.cachedMap.get(url);
    this.resolvedSearchQuery = cachedResult.resolvedSearchQuery;
    this.renderSearchResults(cachedResult.responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    // Indicate that loading has started
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then(async (responseText) => {
        let resolvedSearchQuery = new URL(url).searchParams.get('q') || this.getFocusedSearchQuery();

        // If a strict title search has no products, retry once with Shopify's
        // broader search. This keeps aliases and natural category phrases useful.
        if (!this.responseHasProducts(responseText) && resolvedSearchQuery.startsWith('title:')) {
          const fallbackUrl = new URL(url);
          resolvedSearchQuery = this.getQuery().replace(/\s+/g, ' ');
          fallbackUrl.searchParams.set('q', resolvedSearchQuery);
          const fallbackResponse = await fetch(fallbackUrl);
          if (fallbackResponse.ok) {
            responseText = await fallbackResponse.text();
          }
        }

        this.resolvedSearchQuery = resolvedSearchQuery;
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, { responseText, resolvedSearchQuery });
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        // Remove the loading attribute in both success and error cases
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
        this.setAttribute('results', 'true');
      });
  }
  responseHasProducts(responseText) {
    const parser = new DOMParser();
    const parsedDoc = parser.parseFromString(responseText, 'text/html');
    return Boolean(parsedDoc.querySelector('.predictive-search-result--products .predictive-search__list-item'));
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);
